import { useMemo, useState } from "react";
import type { PropsWithChildren, ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AlertTriangle, LogOut, Menu, Terminal } from "lucide-react";

import { APP_ROUTES, type AppRouteDefinition } from "@/app/navigation";
import { LiveStreamsBridge } from "@/components/live-streams-bridge";
import { ThemeToggle } from "@/components/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { TradingModeBadge } from "@/features/trading/components/TradingModeBadge";
import { KillSwitchStatus } from "@/features/trading/components/KillSwitchStatus";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { apiPost } from "@/lib/api-client";
import { cn } from "@/lib/cn";

interface AppShellProps extends PropsWithChildren {
  activeRouteKey: string;
  username?: string;
}

const SECTION_ORDER = ["core", "analysis", "operations"] as const;
const SECTION_LABELS: Record<(typeof SECTION_ORDER)[number], string> = {
  core: "Core",
  analysis: "Analysis",
  operations: "Operations",
};

function NavLinks({
  groupedRoutes,
  activeRouteKey,
  onNavigate,
}: {
  groupedRoutes: { section: string; routes: AppRouteDefinition[] }[];
  activeRouteKey: string;
  onNavigate?: () => void;
}): ReactNode {
  return (
    <>
      {groupedRoutes.map((group) => (
        <section key={group.section}>
          <h3 className="mb-1 text-[0.7rem] uppercase tracking-wider text-muted/70">
            {SECTION_LABELS[group.section as keyof typeof SECTION_LABELS]}
          </h3>
          <ul className="space-y-0.5">
            {group.routes.map((route) => {
              const Icon = route.icon;
              return (
                <li key={route.key}>
                  <NavLink
                    to={route.canonicalPath}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2.5 rounded-lg border border-transparent px-2.5 py-2 text-[0.84rem] text-muted transition-all hover:border-brand-500/25 hover:text-text",
                        (isActive || activeRouteKey === route.key) &&
                          "border-brand-500/30 bg-brand-500/8 text-brand-300",
                      )
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0 opacity-70" />
                    {route.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </>
  );
}

export function AppShell({ activeRouteKey, username, children }: AppShellProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sheetOpen, setSheetOpen] = useState(false);

  const groupedRoutes = useMemo(() => {
    return SECTION_ORDER.map((section) => ({
      section,
      routes: APP_ROUTES.filter((route) => route.section === section),
    }));
  }, []);

  const logoutMutation = useMutation({
    mutationFn: () => apiPost("/api/auth/logout"),
    onSuccess: () => {
      // Drop ALL cached queries (positions, P&L, balances included) so the
      // next login renders server truth, not the previous session's cache.
      queryClient.clear();
      navigate("/login.html", { replace: true });
    },
    // No onError navigation: server-side session deletion is the sole source
    // of truth. On failure the session is still LIVE — show a loud inline
    // warning (below) and let the operator retry; never simulate a sign-out.
  });

  return (
    <div className="grid min-h-screen md:grid-cols-[18rem_1fr]">
      {/* Push-driven cache invalidation for every authenticated page. */}
      <LiveStreamsBridge />
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex md:flex-col border-r border-border/85 bg-panel p-5"
        aria-label="Primary navigation"
      >
        <div className="mb-5 flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-full border border-brand-500/55 bg-brand-500/12 text-[0.7rem] font-bold text-brand-300">
            NN
          </div>
          <div>
            <div className="text-[0.95rem] font-bold">Neural Net Ninja</div>
            <div className="text-[0.72rem] text-muted">Trading Dashboard</div>
          </div>
        </div>

        <nav
          className="flex flex-1 flex-col gap-4 overflow-y-auto"
          aria-label="Primary navigation links"
        >
          <NavLinks groupedRoutes={groupedRoutes} activeRouteKey={activeRouteKey} />
        </nav>
        {/* Build version — stamped by the deploy workflow (VITE_APP_VERSION); "dev" locally. */}
        <div className="mt-3 border-t border-border/60 pt-2 text-[0.65rem] text-muted/60">
          {import.meta.env.VITE_APP_VERSION ?? "dev"}
        </div>
      </aside>

      {/* Mobile sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="left" className="w-72 bg-background p-5">
          <SheetTitle className="mb-4 flex items-center gap-3">
            <div className="grid h-8 w-8 place-items-center rounded-full border border-brand-500/55 bg-brand-500/12 text-[0.65rem] font-bold text-brand-300">
              NN
            </div>
            <span className="text-sm font-bold">Neural Net Ninja</span>
          </SheetTitle>
          <SheetDescription className="sr-only">
            Main navigation menu
          </SheetDescription>
          <nav className="flex flex-col gap-4">
            <NavLinks
              groupedRoutes={groupedRoutes}
              activeRouteKey={activeRouteKey}
              onNavigate={() => setSheetOpen(false)}
            />
          </nav>
        </SheetContent>
      </Sheet>

      <div className="min-w-0">
        {/* Topbar */}
        <header className="flex flex-wrap items-center justify-between gap-2 md:gap-3 border-b border-border/80 bg-panel px-3 md:px-5 py-2 md:py-3">
          <div className="flex items-center gap-2 md:gap-3">
            {/* Mobile hamburger */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setSheetOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div>
              <p className="m-0 text-sm md:text-base font-semibold text-text">Neural Net Ninja</p>
              <p className="m-0 hidden sm:block text-[0.8rem] text-muted">
                Trading Dashboard
              </p>
            </div>
            <TradingModeBadge />
            {/* Halt state belongs next to the mode badge on EVERY page, not
                only /terminal — an engaged kill switch is the single most
                important operator fact and must never be off-screen. */}
            <KillSwitchStatus />
            {/* KillSwitchStatus is read-only. The ACTION surfaces (/remote
                emergency stop, /terminal) are full-screen routes outside the
                sidebar nav — these links keep them one tap away from every
                page at every viewport. */}
            <Button asChild variant="destructive" size="sm">
              <Link to="/remote" aria-label="Emergency stop controls">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Emergency</span>
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm">
              <Link to="/terminal" aria-label="Open trading terminal">
                <Terminal className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Terminal</span>
              </Link>
            </Button>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <ThemeToggle />
            <Separator orientation="vertical" className="hidden sm:block h-5" />
            <Badge variant="outline" className="hidden sm:inline-flex">
              {username ? `${username}` : "Not authenticated"}
            </Badge>
            <Separator orientation="vertical" className="hidden sm:block h-5" />
            {logoutMutation.isError ? (
              <span role="alert" className="text-xs font-semibold text-red-400">
                Sign-out failed — you are still signed in. Retry.
              </span>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{logoutMutation.isPending ? "Signing out..." : "Sign out"}</span>
            </Button>
          </div>
        </header>

        <main className="p-3 md:p-5">{children}</main>
      </div>
    </div>
  );
}
